/**
 * AbilityEffect — the DSL that card text compiles into. Cards in any
 * platform game ship with a `parsed_abilities` field that is an array
 * of these effect trees. The runtime interpreter walks the tree to
 * apply effects; the content-compiler agent at ingest time is
 * responsible for translating raw card text into this shape.
 *
 * Versioned via the `v` discriminator on the top-level `Ability` type.
 * When the DSL grows breaking changes, bump v1 → v2 and let D1's
 * `cards.ingest_version` track which generation each row is in. We do
 * not write backwards-compatibility shims — `ingest_version=v1` rows
 * are re-ingested through Batch when v2 lands. Per the project doctrine
 * (no half-finished implementations, no shims for unshipped code).
 *
 * For M6.1 we only ship v1. The interpreter is M6.6.
 */
import { Schema as S } from 'effect';
// ─── References ─────────────────────────────────────────────────────
//
// References are resolved at interpretation time against current state
// and the trigger event. They DON'T encode the actual entity id at
// compile time — that depends on game position when the effect fires.
export const PlayerRefSchema = S.Union(S.Literal('self'), // the controller of the ability
S.Literal('opponent'), S.Literal('triggering_player'));
export const TargetRefSchema = S.Union(S.Literal('self'), // the unit/card that has the ability
S.Literal('triggering_target'), // the unit affected by the triggering event
S.Literal('equipped'), // the Wonder this Item is equipped to (Item-only)
S.Struct({
    type: S.Literal('chosen'),
    by: PlayerRefSchema,
    /** Optional natural-language description shown in the picker UI. */
    prompt: S.optional(S.String),
}), 
// Multi-target: every card matching a zone/owner combination ("banish all
// cards target player owns in The Abyss"). The interpreter expands this
// at apply time and applies the effect to each.
//
// v1.7: optional filters narrow the matched set. "Boundless Wonders you
// control" is `{ all_in_realm, side:'allied', realm:'any', orbital:'Boundless' }`.
// All filters are AND-combined; missing means "no constraint on that field".
S.Struct({
    type: S.Literal('all_in_zone'),
    zone: S.Literal('hand', 'deck', 'abyss', 'realm', 'banished'),
    owner: PlayerRefSchema,
    orbital: S.optional(S.String),
    class: S.optional(S.String),
    kind: S.optional(S.Literal('Wonder', 'Land', 'Spell', 'Item', 'Token')),
    has_keyword: S.optional(S.String),
}), 
// Multi-target by realm position + side ("destroy all enemy Wonders here").
S.Struct({
    type: S.Literal('all_in_realm'),
    side: S.Literal('allied', 'enemy', 'any'),
    realm: S.Literal('this', 'adjacent', 'any'),
    orbital: S.optional(S.String),
    class: S.optional(S.String),
    kind: S.optional(S.Literal('Wonder', 'Land', 'Spell', 'Item', 'Token')),
    has_keyword: S.optional(S.String),
}));
export const NumExprSchema = S.suspend(() => S.Union(S.Struct({ _tag: S.Literal('lit'), value: S.Number }), S.Struct({
    _tag: S.Literal('count'),
    of: S.String,
    player: PlayerRefSchema,
}), S.Struct({ _tag: S.Literal('add'), left: NumExprSchema, right: NumExprSchema }), S.Struct({ _tag: S.Literal('subtract'), left: NumExprSchema, right: NumExprSchema }), S.Struct({ _tag: S.Literal('multiply'), left: NumExprSchema, right: NumExprSchema })));
export const BoolExprSchema = S.suspend(() => S.Union(S.Struct({ _tag: S.Literal('has_keyword'), target: TargetRefSchema, keyword: S.String }), S.Struct({
    _tag: S.Literal('controls'),
    player: PlayerRefSchema,
    count: NumExprSchema,
    of: S.String,
}), S.Struct({ _tag: S.Literal('in_high_realm'), target: TargetRefSchema }), S.Struct({ _tag: S.Literal('has_orbital'), target: TargetRefSchema, orbital: S.String }), S.Struct({
    _tag: S.Literal('has_counter'),
    target: TargetRefSchema,
    counter: CounterKindSchema,
    count: S.optional(NumExprSchema),
}), S.Struct({ _tag: S.Literal('and'), conds: S.Array(BoolExprSchema) }), S.Struct({ _tag: S.Literal('or'), conds: S.Array(BoolExprSchema) }), S.Struct({ _tag: S.Literal('not'), cond: BoolExprSchema })));
// ─── Counter kinds ──────────────────────────────────────────────────
// CounterKind is a string at the schema layer (v1.6). Cards introduce
// custom counter kinds frequently (e.g. meat, spin, nano) and enumerating
// them in the schema would mean a re-batch every time a new set ships.
// The interpreter validates against a runtime registry instead.
//
// Documented canonical kinds (the rules-skill teaches these to the
// compiler so it picks them over invented synonyms):
//   plus_one | minus_one | shield | null | protector | growth | celestial
export const CounterKindSchema = S.String;
// ─── Event patterns (for triggered abilities) ───────────────────────
//
// "Whenever an enemy Wonder enters a realm you control, draw a card."
// The pattern matches the triggering event; the interpreter binds
// `triggering_player`/`triggering_target` for use in the effect body.
export const EventPatternSchema = S.Union(S.Struct({ on: S.Literal('round_start'), round: S.optional(S.Number) }), S.Struct({ on: S.Literal('round_end'), round: S.optional(S.Number) }), S.Struct({
    on: S.Literal('arrival'),
    kind: S.optional(S.Literal('Wonder', 'Land', 'Spell', 'Item')),
}), S.Struct({ on: S.Literal('attack'), where: S.optional(S.Literal('any_realm', 'this_realm')) }), S.Struct({ on: S.Literal('battle'), where: S.optional(S.Literal('any_realm', 'this_realm')) }), S.Struct({ on: S.Literal('destroy'), kind: S.optional(S.Literal('Wonder', 'Land', 'Item')) }), S.Struct({ on: S.Literal('draw_card') }), S.Struct({ on: S.Literal('cast_spell') }), S.Struct({ on: S.Literal('harvest') }), // §7 Harvest — when a Land is harvested
S.Struct({
    on: S.Literal('battle_won'),
    where: S.optional(S.Literal('any_realm', 'this_realm')),
}), S.Struct({ on: S.Literal('damage_dealt') }), S.Struct({ on: S.Literal('spend_faith') }), // §2 Faith — fires per faith spent
S.Struct({ on: S.Literal('become_equipped') }), // Item attaches to a Wonder
S.Struct({ on: S.Literal('seize_stone') }), 
// ── v1.6 additions: more keyword-derived events observed in card text ──
S.Struct({ on: S.Literal('move') }), S.Struct({ on: S.Literal('sacrifice') }), S.Struct({ on: S.Literal('eternal') }), // §7 Eternal — fires when card moves deck → abyss
S.Struct({ on: S.Literal('awaken') }), // §7 Awaken — fires when keyword activates
S.Struct({ on: S.Literal('nullified') }), S.Struct({ on: S.Literal('receive_counter'), counter: S.optional(CounterKindSchema) }), S.Struct({ on: S.Literal('gain_counter'), counter: S.optional(CounterKindSchema) }), S.Struct({ on: S.Literal('gain_resource'), kind: S.optional(S.String) }), S.Struct({ on: S.Literal('gain_action') }), S.Struct({ on: S.Literal('opponent_pass') }), S.Struct({ on: S.Literal('banish') }), S.Struct({ on: S.Literal('create_token') }), S.Struct({ on: S.Literal('tap_ability_used') }), S.Struct({ on: S.Literal('seek') }), S.Struct({ on: S.Literal('reveal') }), S.Struct({ on: S.Literal('tap') }));
// ─── Helper schemas: TokenSpec and CardFilter ───────────────────────
//
// Used by CreateToken, Seek, Call, ModifyCost. Defined as standalone
// schemas (not recursive into AbilityEffect) so they don't need
// S.suspend.
export const TokenSpecSchema = S.Struct({
    /** Display name of the token (for UI + interpreter naming). */
    name: S.String,
    /** Power for Wonder tokens. Omit for non-Wonder token types. */
    power: S.optional(S.Number),
    /** Card classes to inherit ("Construct", "Fungorian", etc.). */
    classes: S.optional(S.Array(S.String)),
    /** Keywords granted to the token at creation. */
    keywords: S.optional(S.Array(S.String)),
    /** "Create a token COPY of target Wonder" — interpreter copies abilities. */
    copy_of: S.optional(TargetRefSchema),
    /** Default `permanent`; `end_of_round` for "sacrifice at end of round" tokens. */
    duration: S.optional(S.Literal('end_of_round', 'permanent')),
});
export const CardFilterSchema = S.Struct({
    kind: S.optional(S.Literal('Wonder', 'Land', 'Spell', 'Item', 'Token')),
    max_cost: S.optional(NumExprSchema),
    classes: S.optional(S.Array(S.String)),
    /** One of the six Wonders orbitals (§4). */
    orbital: S.optional(S.String),
    /** Filter by a granted/innate keyword. */
    has_keyword: S.optional(S.String),
});
/** Inline destination schema — not exported because every consumer is in this file. */
const DestinationSchema = S.Union(S.Struct({
    kind: S.Literal('realm'),
    which: S.Literal('this', 'adjacent', 'chosen', 'specific'),
    realm: S.optional(S.String),
}), S.Struct({
    kind: S.Literal('zone'),
    zone: S.Literal('hand', 'abyss', 'deck', 'banished'),
    player: PlayerRefSchema,
}));
export const AbilityEffectSchema = S.suspend(() => S.Union(S.Struct({ op: S.Literal('Damage'), target: TargetRefSchema, amount: NumExprSchema }), S.Struct({ op: S.Literal('Heal'), target: TargetRefSchema, amount: NumExprSchema }), S.Struct({ op: S.Literal('Draw'), player: PlayerRefSchema, n: NumExprSchema }), S.Struct({ op: S.Literal('Discard'), player: PlayerRefSchema, n: NumExprSchema }), S.Struct({
    op: S.Literal('AddCounter'),
    target: TargetRefSchema,
    counter: CounterKindSchema,
    n: NumExprSchema,
}), S.Struct({
    op: S.Literal('RemoveCounter'),
    target: TargetRefSchema,
    counter: CounterKindSchema,
    n: NumExprSchema,
}), S.Struct({ op: S.Literal('Destroy'), target: TargetRefSchema }), S.Struct({ op: S.Literal('Banish'), target: TargetRefSchema }), S.Struct({ op: S.Literal('Sacrifice'), target: TargetRefSchema }), S.Struct({ op: S.Literal('GrantKeyword'), target: TargetRefSchema, keyword: S.String }), S.Struct({
    op: S.Literal('GainResource'),
    player: PlayerRefSchema,
    kind: S.String,
    amount: NumExprSchema,
}), 
// ── v1.5 additions ──
S.Struct({ op: S.Literal('Tap'), target: TargetRefSchema }), S.Struct({ op: S.Literal('Untap'), target: TargetRefSchema }), S.Struct({ op: S.Literal('Stun'), target: TargetRefSchema }), S.Struct({ op: S.Literal('Deny'), player: PlayerRefSchema, n: NumExprSchema }), S.Struct({ op: S.Literal('Harvest'), target: TargetRefSchema }), S.Struct({
    op: S.Literal('CreateToken'),
    token: TokenSpecSchema,
    where: S.Literal('this_realm', 'adjacent_realm', 'chosen_realm'),
    n: NumExprSchema,
}), S.Struct({
    op: S.Literal('Move'),
    target: TargetRefSchema,
    to: DestinationSchema,
}), S.Struct({
    op: S.Literal('Return'),
    target: TargetRefSchema,
    from: S.Struct({
        zone: S.Literal('abyss', 'deck', 'hand', 'banished'),
        player: PlayerRefSchema,
    }),
    to: DestinationSchema,
}), S.Struct({
    op: S.Literal('Seek'),
    n: NumExprSchema,
    filter: S.optional(CardFilterSchema),
}), S.Struct({
    op: S.Literal('Call'),
    to: S.Literal('this_realm', 'chosen_realm'),
    filter: S.optional(CardFilterSchema),
}), S.Struct({
    op: S.Literal('ModifyPower'),
    target: TargetRefSchema,
    delta: NumExprSchema,
    duration: S.Literal('this_round', 'permanent', 'while_in_play'),
}), S.Struct({
    op: S.Literal('ModifyCost'),
    filter: CardFilterSchema,
    delta: NumExprSchema,
    duration: S.Literal('this_round', 'permanent', 'while_in_play'),
}), S.Struct({
    op: S.Literal('GainControl'),
    target: TargetRefSchema,
    duration: S.Literal('this_round', 'permanent', 'while_in_play'),
}), S.Struct({
    op: S.Literal('Shuffle'),
    target: TargetRefSchema,
    into: S.Struct({ player: PlayerRefSchema, zone: S.Literal('deck') }),
}), S.Struct({
    op: S.Literal('Reveal'),
    source: S.Struct({
        player: PlayerRefSchema,
        zone: S.Literal('deck'),
        position: S.Literal('top'),
    }),
    n: NumExprSchema,
}), 
// ── v1.6 additions ──
S.Struct({
    op: S.Literal('ModifyHandSize'),
    player: PlayerRefSchema,
    delta: NumExprSchema,
    duration: S.Literal('this_round', 'permanent', 'while_in_play'),
}), S.Struct({ op: S.Literal('RemoveKeyword'), target: TargetRefSchema, keyword: S.String }), S.Struct({ op: S.Literal('AddClass'), target: TargetRefSchema, classes: S.Array(S.String) }), 
// ── recursive composites ──
S.Struct({ op: S.Literal('Trigger'), on: EventPatternSchema, do: AbilityEffectSchema }), S.Struct({
    op: S.Literal('Choose'),
    player: PlayerRefSchema,
    from: S.Array(AbilityEffectSchema),
}), S.Struct({ op: S.Literal('Sequence'), effects: S.Array(AbilityEffectSchema) }), S.Struct({
    op: S.Literal('When'),
    cond: BoolExprSchema,
    // biome-ignore lint/suspicious/noThenProperty: AbilityEffect.When uses `then` as the if-true branch — pure data, not Promise-related.
    then: AbilityEffectSchema,
    else: S.optional(AbilityEffectSchema),
})));
// ─── Top-level Ability (versioned wrapper) ──────────────────────────
//
// A card's parsed_abilities is `Ability[]`. The version tag lets us
// store v1 and future-v2 rows side by side in D1 and route each to
// the right interpreter.
export const AbilitySchema = S.Struct({
    v: S.Literal(1), // bump to literal union when v2 ships
    effect: AbilityEffectSchema,
    /**
     * Optional human-readable note. Useful for ambiguous-card review:
     * the content-compiler can write "auto-translation; flagged for
     * human review" here when its confidence is low.
     */
    notes: S.optional(S.String),
});
//# sourceMappingURL=abilities.js.map